import { MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { Typography } from '../../../../design-system/components/typography/Typography';
import { SearchSubmissionTarget } from '../../SearchOverlay.types';
import { FallbackRow } from '../../utils/fallbackRows';

import { FallbackRowItem } from './FallbackRowItem';
import style from './SearchNoResultsState.module.css';

type Props = {
    query: string;
    rows: FallbackRow[];
    activeIndex: number | null;
    pendingIndex: number | null;
    itemRef: (index: number, el: HTMLAnchorElement | null) => void;
    onMouseEnter: (index: number) => void;
    getOptionId: (index: number) => string;
    onSelect: (target: SearchSubmissionTarget, index: number) => void;
};

export function SearchNoResultsState({
    query,
    rows,
    activeIndex,
    pendingIndex,
    itemRef,
    onMouseEnter,
    getOptionId,
    onSelect,
}: Props) {
    const { formatMessage } = useIntl();

    const handleRowClick = (
        event: MouseEvent<HTMLAnchorElement>,
        target: SearchSubmissionTarget,
        index: number,
    ) => {
        event.preventDefault();
        if (pendingIndex === null) onSelect(target, index);
    };

    const firstTopDestinationIndex = rows.findIndex((row) => row.kind === 'top-destination');
    const fixedRows =
        firstTopDestinationIndex === -1 ? rows : rows.slice(0, firstTopDestinationIndex);
    const topRows = firstTopDestinationIndex === -1 ? [] : rows.slice(firstTopDestinationIndex);

    return (
        <div className={style.noResults}>
            <Typography as="p" scale="primary-xs-regular" className={style.noResultsMessage}>
                {formatMessage({ id: 'global.search.overlay.noResults.message' }, { query })}
            </Typography>
            <div className={style.list}>
                {fixedRows.map((row, index) => (
                    <FallbackRowItem
                        key={row.key}
                        row={row}
                        id={getOptionId(index)}
                        isActive={activeIndex === index}
                        isPending={pendingIndex === index}
                        isDisabled={pendingIndex !== null}
                        itemRef={(el) => itemRef(index, el)}
                        onMouseEnter={() => onMouseEnter(index)}
                        onClick={(event) => handleRowClick(event, row.target, index)}
                    />
                ))}
            </div>
            {topRows.length > 0 ? (
                <div className={style.section}>
                    <div className={style.header}>
                        <Typography scale="primary-xs-bold" className={style.title}>
                            {formatMessage({
                                id: 'global.header.globalNav.destinations.topDestinations.world',
                            })}
                        </Typography>
                    </div>
                    <div className={style.list}>
                        {topRows.map((row, localIndex) => {
                            const index = fixedRows.length + localIndex;
                            return (
                                <FallbackRowItem
                                    key={row.key}
                                    row={row}
                                    id={getOptionId(index)}
                                    isActive={activeIndex === index}
                                    isPending={pendingIndex === index}
                                    isDisabled={pendingIndex !== null}
                                    itemRef={(el) => itemRef(index, el)}
                                    onMouseEnter={() => onMouseEnter(index)}
                                    onClick={(event) => handleRowClick(event, row.target, index)}
                                />
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
